/**
 * AppSubNav — React port of AngularJS SubnavController + subnav.html
 *
 * Source: public/layout/subnav.html (747 lines) + public/layout/subnav.js (80 lines)
 *
 * Context-sensitive sub-navigation bar that displays different menu items
 * based on `useSiteContextStore().currentContext`. This is the React
 * equivalent of the AngularJS `ng-if="nav === 'property'"` blocks.
 *
 * All menu items, permissions, badges, and active-state logic are a 1:1 port
 * of the AngularJS template.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSiteContextStore } from './useSiteContextStore';
import { useLegacyPermissions } from '@/lib/useLegacyPermissions';
import { getLegacyWindow } from '@/lib/legacyContext';
import { usePropertyStore, useWorkOrder, useProperty } from '@/store/propertyStore';
import {
  useDisputesCount,
  useReturnUrlInvoice,
  useClientCodeDetail,
  useClientCode,
  useActiveServiceAreaId,
  useCompanyDetails,
} from '@/store';
import { useModalStore } from '@/store/modalStore';
import { useNavContextStore } from '@/store/navContextStore';
import { useCountService } from '@/features/auditDashboard/useCountService';
import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';
import { getPropertyHistoryWorkOrders } from '@/lib/propertyHistoryApi';
import { getPropInfoByPropertyLoanId } from '@/features/search/searchApi';
import { listTextHeaders } from '@/features/messageDashboard/textApi';
import { listDisputeSummary } from '@/features/disputes/disputesApi';

const API_BASE = buildApiBase();

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Mirrors AngularJS `$scope.isActive(viewLocation)`:
 *   `viewLocation === $location.path()`
 *
 * `window.location.hash` is `#/Some/Path` — strip the leading `#` to get the
 * path portion comparable to `$location.path()`.
 */
function isActive(path: string): boolean {
  const hash = window.location.hash.replace(/^#/, '').split('?')[0];
  return hash === path;
}

/**
 * Port of `$scope.newWindow` → `common.openInNewWindow` from AngularJS
 * (`public/index.js` + `public/SPIGlassApp/common/common.js`). Opens at
 * 980x800,left=100 so browsers render it as a popup window, not a new tab.
 */
function newWindow(url: string): void {
  window.open(url, '', 'width=980,height=800,left=100');
}

/**
 * Port of `$scope.supportedReconveyLoanType()`.
 * Checks if G_PROPERTY.loanType is one of FHA, FNM, FMC.
 */
function supportedReconveyLoanType(): boolean {
  const prop = usePropertyStore.getState().property;
  if (!prop?.loanType) return false;
  return ['FHA', 'FNM', 'FMC'].includes(prop.loanType);
}

/**
 * Map of hash path → activetab metadata, ported from `activetab` entries in
 * public/SPIGlassApp/config.route.js. AngularJS defines this on each route
 * config and we read it via `$route.current.activetab`. In React we look it
 * up from the current `window.location.hash`.
 *
 * Only entries actually referenced in this subnav are mapped — adding more is
 * cheap if other contexts need them.
 */
const ROUTE_ACTIVE_TAB_PATTERNS: Array<{ test: RegExp; tab: string }> = [
  // VendorManagement
  { test: /^\/VendorManagement\/VendorManagement/, tab: 'VendorMGMT' },
  {
    test: /^\/VendorManagement\/(UnlockMobileDevices|UploadIneligibleVendorList|SelfServiceOrders|RemoveAllServiceOrders|UploadRankingList|RankFileManagement|VendorRegions|ZoneProfiles|VendorMemos)/,
    tab: 'Utilities',
  },
  { test: /^\/VendorManagement\/(UploadOppMapsList|UploadZonesList)/, tab: 'Marketing' },
  { test: /^\/VendorManagement\/UnlockUser/, tab: 'VMAdmin' },
  // Disputes
  { test: /^\/Disputes\//, tab: 'Disputes' },
  // Admin/Clients
  { test: /^\/Admin\/Clients\/ClientScheduleOptions/, tab: 'ClientScheduleOptions' },
  { test: /^\/Admin\/Clients\/ClientAllowables/, tab: 'ClientAllowables' },
  // Pricing
  { test: /^\/Admin\/Pricing\/ChangeSource/, tab: 'ChangeSource' },
  {
    test: /^\/Admin\/Pricing\/ClientPricing\/(ClientTaskRateLookup|DefaultRate|ClientTaskmap|ClientLoanTypemap)/,
    tab: 'ClientRate',
  },
  { test: /^\/Admin\/Pricing\/TestRendering/, tab: 'TestRendering' },
  // ClientGrouping
  { test: /^\/Admin\/ClientGrouping\/StateGroup/, tab: 'StateGroup' },
  { test: /^\/Admin\/ClientGrouping\/CityGroup/, tab: 'CityGroup' },
  { test: /^\/Admin\/ClientGrouping\/CountyGroup/, tab: 'CountyGroup' },
  { test: /^\/Admin\/ClientGrouping\/ZipCodeGroup/, tab: 'ZipCodeGroup' },
  { test: /^\/Admin\/ClientGrouping\/WorkCodeGroup/, tab: 'WorkCodeGroup' },
  { test: /^\/Admin\/ClientGrouping\/AttributeGrouping/, tab: 'AttributeGroup' },
  { test: /^\/Admin\/ClientGrouping\/PunchCodeGroup/, tab: 'PunchCodeGroup' },
  // Scheduling
  { test: /^\/Admin\/Scheduling\/StandardSetup/, tab: 'StandardSetup' },
  // Allowables
  { test: /^\/Admin\/Allowables\/AllowableGroup/, tab: 'AllowableGroup' },
  { test: /^\/Admin\/Allowables\/AllowableCategories/, tab: 'AllowableCategories' },
  { test: /^\/Admin\/Allowables\/AllowableTaskCategories/, tab: 'AllowableTaskCategories' },
  { test: /^\/Admin\/Allowables\/AllowableServices/, tab: 'AllowableServices' },
  { test: /^\/Admin\/Allowables\/AllowableTasks/, tab: 'Allowable' },
];

/** Derive activetab metadata from the current hash (replaces `$route.current.activetab`). */
function getActiveTab(): string {
  const hash = window.location.hash.replace(/^#/, '').split('?')[0];
  for (const { test, tab } of ROUTE_ACTIVE_TAB_PATTERNS) {
    if (test.test(hash)) return tab;
  }
  return '';
}

/** Check if currently on /OrderEntry route. */
function isOnOrderEntryPage(): boolean {
  const hash = window.location.hash.replace(/^#/, '').split('?')[0];
  return hash === '/OrderEntry';
}

/** Port of $scope.clearSearch — clears AngularJS $rootScope search state (embedded only). */
function clearSearch(): void {
  try {
    const win = getLegacyWindow();
    const root = win.$rootScope as Record<string, unknown> | undefined;
    if (root) {
      root.GetSearch = null;
      root.responseInfo = { responseMessage: '', status: '' };
    }
  } catch {
    // ignore — standalone mode (no AngularJS scope to clear)
  }
}

/**
 * Port of $scope.clearQueryParams — strips the `?...` portion of the current
 * hash, equivalent to `$location.search({})` in AngularJS.
 */
function clearQueryParams(): void {
  const [path] = window.location.hash.replace(/^#/, '').split('?');
  if (window.location.hash !== `#${path}`) {
    window.location.hash = `#${path}`;
  }
}

/**
 * Fetch the property work-orders array via the React-native API
 * (port of propertyHistoryService.refreshPropertyHistory + the
 * `propertyHistory.workOrders` cache). Returns [] when no property is selected
 * or the request fails.
 */
async function fetchPropertyWorkOrders(spiPropertyId: number | string): Promise<unknown[]> {
  try {
    return await getPropertyHistoryWorkOrders(spiPropertyId);
  } catch {
    return [];
  }
}

/**
 * Fetch a deduplicated list of work orders that have texts via the React-native
 * API (port of textService.workOrdersWithTexts which was populated by
 * generalDataService.getTextHeaders → /API/Text/ListTextHeaders).
 */
async function fetchWorkOrdersWithTexts(
  spiPropertyId: number | string
): Promise<Array<{ orderNumber: string }>> {
  try {
    const headers = await listTextHeaders(Number(spiPropertyId));
    const seen = new Set<string>();
    const unique: Array<{ orderNumber: string }> = [];
    for (const th of headers ?? []) {
      const num = th.orderNumber != null ? String(th.orderNumber) : '';
      if (num && !seen.has(num)) {
        seen.add(num);
        unique.push({ orderNumber: num });
      }
    }
    return unique;
  } catch {
    return [];
  }
}

// ─── Dropdown hook — click-outside closes the menu ──────────────────────────

function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  function clearCloseTimer() {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  useEffect(() => () => clearCloseTimer(), []);

  // Outside-click closer (kept as a fallback for click-to-open paths and
  // touch devices where mouseleave never fires).
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [open]);

  // Phase B (issue #6) — auto-close when the user navigates. AppSubNav lives
  // in the persistent layout shell, so React state survives route changes;
  // without this, clicking a sub-item leaves the dropdown stuck open after
  // the new route renders.
  useEffect(() => {
    if (!open) return;
    function onHashChange() {
      setOpen(false);
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [open]);

  // Hover handlers — open immediately on mouseenter, close after a short
  // delay on mouseleave so the cursor can travel between the parent <li>
  // and the dropdown <ul> without the menu collapsing mid-traverse.
  const onMouseEnter = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 150);
  }, []);

  // Click still toggles (kept for back-compat + touch / keyboard users) but
  // never preventDefault — the parent `<a href="#/...">` must navigate.
  const toggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const close = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, []);

  return { open, toggle, ref, onMouseEnter, onMouseLeave, close };
}

// ─── Subnav API calls (fetched once) ────────────────────────────────────────

function useSubnavUrls(perms: Record<string, unknown>) {
  const [imageViewerUrl, setImageViewerUrl] = useState('');
  const [fileImporterURL, setFileImporterURL] = useState('');

  useEffect(() => {
    if (perms.SGStaff) {
      axiosClient
        .get<string>(`${API_BASE}/API/Partial/GetImageViewerURL/`)
        .then((r) => setImageViewerUrl(r.data))
        .catch(() => {});
    }
  }, [perms.SGStaff]);

  useEffect(() => {
    if (perms.SafeSend) {
      axiosClient
        .get<string>(`${API_BASE}/API/Partial/GetFileImporterURL`)
        .then((r) => setFileImporterURL(r.data))
        .catch(() => {});
    }
  }, [perms.SafeSend]);

  const communicationToolURL = `https://vc.${window.location.hostname.split('.').slice(1).join('.')}`;

  return { imageViewerUrl, fileImporterURL, communicationToolURL };
}

// ─── AppSubNav Component ────────────────────────────────────────────────────

export function AppSubNav() {
  const currentContext = useSiteContextStore((s) => s.currentContext);
  const perms = useLegacyPermissions();
  const workOrder = useWorkOrder();
  const property = useProperty();
  const counts = useCountService();
  const { imageViewerUrl, fileImporterURL, communicationToolURL } = useSubnavUrls(
    perms as unknown as Record<string, unknown>
  );

  // Re-read active state on hash change — `activeTab` is derived from the hash
  // via ROUTE_ACTIVE_TAB_PATTERNS so we recompute whenever the hash changes.
  const [activeTab, setActiveTab] = useState(() => getActiveTab());
  useEffect(() => {
    function onHashChange() {
      setActiveTab(getActiveTab());
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Fetch property work-orders and text-headers via React-native APIs.
  // Mirrors AngularJS propertyHistoryService.refreshPropertyHistory +
  // textService.LoadTexts triggered by propertySelectedService.storePropInfo
  // (propertySelectedService.js:370). Both endpoints require
  // G_PROPERTY.spiPropertyId (NOT workOrder.propertyLoanId — those are
  // different IDs; using the wrong one returns 401 from the backend).
  const [workOrders, setWorkOrders] = useState<unknown[]>([]);
  const [workOrdersWithTexts, setWorkOrdersWithTexts] = useState<unknown[]>([]);
  const spiPropertyId = property?.spiPropertyId;
  const workOrderPropertyLoanId = workOrder?.propertyLoanId;

  // First-load fix: if a workOrder is present but `property` (and therefore
  // spiPropertyId) hasn't been loaded into the store yet, fetch it lazily here.
  // Without this, on first navigation the "Work Orders" and "View Texts" buttons
  // stay hidden because spiPropertyId is undefined → no API calls fire. The
  // legacy AngularJS app populates G_PROPERTY automatically via
  // propertySelectedService.storeOrder; standalone only does this when the
  // route was reached with ?workorder= or ?propertyid= URL params.
  useEffect(() => {
    if (spiPropertyId != null) return;
    if (workOrderPropertyLoanId == null) return;
    let cancelled = false;
    getPropInfoByPropertyLoanId(workOrderPropertyLoanId)
      .then((propData) => {
        if (cancelled) return;
        const prop = propData as { id?: number } | null;
        if (prop && (prop.id ?? 0) > 0) {
          // setProperty dual-writes to $rootScope.G_PROPERTY in embedded mode
          // and is consumed by useProperty() in this same component on next render.
          usePropertyStore.getState().setProperty(prop as never);
        }
      })
      .catch(() => {
        // Best-effort — if it fails the buttons just stay hidden.
      });
    return () => {
      cancelled = true;
    };
  }, [spiPropertyId, workOrderPropertyLoanId]);

  useEffect(() => {
    if (spiPropertyId == null) {
      setWorkOrders([]);
      setWorkOrdersWithTexts([]);
      return;
    }
    let cancelled = false;
    fetchPropertyWorkOrders(spiPropertyId).then((items) => {
      if (!cancelled) setWorkOrders(items);
    });
    fetchWorkOrdersWithTexts(spiPropertyId).then((items) => {
      if (!cancelled) setWorkOrdersWithTexts(items);
    });
    return () => {
      cancelled = true;
    };
  }, [spiPropertyId]);

  // Disputes badge — port of propertySelectedService.storeOrder()
  // (propertySelectedService.js:322-332): ListDisputeSummary(G_WORKORDER.id)
  // → if any dispute is NOT in 'Close' state, DisputesCount = ' ' (a single
  // space, which renders the badge as an empty red dot via the
  // `.top-right-badge[data-badge]:after` CSS); otherwise null (no badge).
  const workOrderId = workOrder?.id;
  useEffect(() => {
    if (workOrderId == null) {
      useNavContextStore.getState().setDisputesCount(null);
      return;
    }
    let cancelled = false;
    listDisputeSummary(String(workOrderId))
      .then((items) => {
        if (cancelled) return;
        // Matches AngularJS `if (data.data != null)` — only update when the
        // response is a valid array; leave the badge untouched otherwise.
        if (Array.isArray(items)) {
          const hasOpenDispute = items.some(
            (x) => x?.workflowStateTransition?.currentState?.code !== 'Close'
          );
          useNavContextStore.getState().setDisputesCount(hasOpenDispute ? ' ' : null);
        }
      })
      .catch(() => {
        // graceful — leave the badge unchanged on failure
      });
    return () => {
      cancelled = true;
    };
  }, [workOrderId]);

  // Read values from Zustand stores
  const disputesCount = useDisputesCount();
  const returnUrlInvoice = useReturnUrlInvoice();
  const clientCodeDetail = useClientCodeDetail();
  const clientCode = useClientCode();
  const activeServiceareaId = useActiveServiceAreaId();
  const companyDetails = useCompanyDetails() as { company?: { id?: number | string } } | null;

  // ── Scope functions (ports of subnav.js + index.js) ──

  const toggleWorkOrderModal = useCallback(() => {
    useModalStore.getState().toggle('workOrders');
  }, []);

  const togglePropertyTextsModal = useCallback(() => {
    useModalStore.getState().toggle('propertyText');
  }, []);

  // Matches legacy subnav.html lines 60-69 and propertyTextPanel.js navigateToAddText.
  // `useCoreScript=true` routes to the V2 (CoreScript) variant: /AddText2/:orderNumber.
  const navigateToAddText = useCallback(
    (useCoreScript: boolean = false) => {
      const orderNumber = workOrder?.orderNumber;
      if (orderNumber) {
        window.location.hash = `#/AddText${useCoreScript ? '2' : ''}/${orderNumber}`;
      }
    },
    [workOrder?.orderNumber]
  );

  const toggleVendorTextsModal = useCallback(() => {
    useModalStore.getState().toggle('vendorText');
  }, []);

  // Matches master public/index.js:626 navigateToAddVendorText(useCoreScript).
  // `useCoreScript=true` routes to /AddVendorText2/{serviceAreaId} (SafeView iframe).
  const navigateToAddVendorText = useCallback(
    (useCoreScript: boolean = false) => {
      if (activeServiceareaId != null) {
        window.location.hash = `#/AddVendorText${useCoreScript ? '2' : ''}/${activeServiceareaId}`;
      }
    },
    [activeServiceareaId]
  );

  const navigateToVendorNotes = useCallback(() => {
    const companyId = companyDetails?.company?.id;
    if (companyId != null) {
      window.location.hash = `#/Notes?vendorCompanyId=${companyId}`;
    }
  }, [companyDetails?.company?.id]);

  // Dropdowns
  const propertyRecordDD = useDropdown();
  const vendorPaymentsDD = useDropdown();
  const overviewDD = useDropdown();
  const damagesDD = useDropdown();
  const bidsDD = useDropdown();
  const vendorPaymentsWoDD = useDropdown();
  const utilitiesDD = useDropdown();
  const marketingDD = useDropdown();
  const vmAdminDD = useDropdown();
  const rateSetupDD = useDropdown();
  const allowableUploadsDD = useDropdown();
  const selfServiceDD = useDropdown();
  const assignmentDD = useDropdown();

  // ── nav === 'hidden' — don't render
  if (currentContext === 'hidden') return null;

  // ── nav == null || nav == '' — empty subnav spacer
  if (!currentContext) {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav navbar-right sub-nav-right'>
          <li>
            <a>&nbsp;</a>
          </li>
        </ul>
        <ul className='nav sub-nav sub-nav-left' />
      </div>
    );
  }

  // ── Shared: Property right items (Work Orders, View Texts, Add Text, Notes)
  const renderPropertyRightItems = (viewTextLabel: string) => (
    <ul className='nav sub-nav navbar-right sub-nav-right'>
      {workOrders.length > 0 && (
        <li className='property-work-orders-init'>
          <a onClick={toggleWorkOrderModal} style={{ cursor: 'pointer' }}>
            <i className='fa fa-file-text-o' />
            <span className='work-order-number'> {workOrders.length}</span> Work Orders
          </a>
        </li>
      )}
      {workOrdersWithTexts.length > 0 && (
        <li className='property-texts-init'>
          <a onClick={togglePropertyTextsModal} style={{ cursor: 'pointer' }}>
            <i className='fa fa-comment-o' style={{ fontSize: '18px' }} /> {viewTextLabel}
          </a>
        </li>
      )}
      {/* Legacy "Add Text" (AddText) removed per PR #204 — the Corescript
          variant below is now the sole path. */}
      {workOrder?.orderNumber && Boolean(perms.addTextCore) && (
        <li className='work-order-text-init'>
          <a onClick={() => navigateToAddText(true)} style={{ cursor: 'pointer' }}>
            <i className='fa fa-plus' /> Add Text Corescript
          </a>
        </li>
      )}
      <li className='property-notes-init'>
        <a href='#/Notes'>
          <i className='fa fa-pencil' /> Notes
        </a>
      </li>
    </ul>
  );

  // ── Shared: Property Record dropdown sub-items
  // `includeReconvey` defaults to true — in the workorder/audit/auditresults context
  // the AngularJS template does NOT include the Reconvey Liability Script item.
  const renderPropertyRecordDropdown = (
    dd: ReturnType<typeof useDropdown>,
    opts?: { includeReconvey?: boolean }
  ) => (
    <li
      ref={dd.ref}
      onMouseEnter={dd.onMouseEnter}
      onMouseLeave={dd.onMouseLeave}
      className={`${isActive('/Property/record') || isActive('/Property/record/costtodate') ? 'active' : ''}${dd.open ? ' open' : ''}`}
    >
      <a href='#/Property/record' className='dropdown-toggle' onClick={dd.toggle}>
        <br />
        Property Record
        <b className='caret' />
      </a>
      {dd.open && (
        <ul className='dropdown-menu' style={{ display: 'block' }}>
          <li>
            <a style={{ borderBottom: '0px' }} href='#/Property/record/conveyimpediments'>
              Convey Impediments
            </a>
          </li>
          {property?.ctdV2Supported === 'N' && (
            <li>
              <a style={{ borderBottom: '0px' }} href='#/Property/record/costtodate'>
                Cost To Date
              </a>
            </li>
          )}
          {perms.allowablesCTD && (
            <li>
              <a style={{ borderBottom: '0px' }} href='#/Property/costtodate'>
                COST TO DATE{property?.ctdV2Supported === 'N' ? ' (BETA)' : ''}
              </a>
            </li>
          )}
          {perms.allowablesCTD && (
            <li>
              <a style={{ borderBottom: '0px' }} href='#/Property/CTDDetails'>
                COST TO DATE Details
              </a>
            </li>
          )}
          {property?.clientCode?.code === 'CHL' && perms.loanQualifier && (
            <li>
              <a style={{ borderBottom: '0px' }} href='#/LoanQualifier'>
                Loan Qualifier Information
              </a>
            </li>
          )}
          {/* Legacy "Create Order" (#/OrderEntry) removed per PR #204 — the
              Core variant below is now the sole path. */}
          {perms.createOrderRequestCore && property?.active === 'Y' && (
            <li>
              <a style={{ borderBottom: '0px' }} href='#/OrderEntry2'>
                Create Order Core
              </a>
            </li>
          )}
          {perms.serviceRelease && (
            <li>
              <a style={{ borderBottom: '0px' }} href='#/Property/ServiceRelease'>
                Service Level History
              </a>
            </li>
          )}
          {property?.active === 'Y' && (
            <li>
              <a style={{ borderBottom: '0px' }} href='#/Property/MergeIds'>
                Merge Loan Ids
              </a>
            </li>
          )}
          {/* Legacy "Reconvey Liability Script" (#/Property/ReconveyRequest)
              removed per PR #204 — the Core Script variant below is now the
              sole path. */}
          {opts?.includeReconvey !== false &&
            perms.auditReconvey2 &&
            supportedReconveyLoanType() && (
              <li>
                <a style={{ borderBottom: '0px' }} href='#/Property/ReconveyRequest2'>
                  Reconvey Liability Core Script
                </a>
              </li>
            )}
        </ul>
      )}
    </li>
  );

  // ── Shared: Vendor Payments dropdown
  const renderVendorPaymentsDropdown = (dd: ReturnType<typeof useDropdown>) =>
    perms.prepaymentReader || perms.vendorPenaltiesReader ? (
      <li
        ref={dd.ref}
        onMouseEnter={dd.onMouseEnter}
        onMouseLeave={dd.onMouseLeave}
        className={`${isActive('/VendorPayments') ? 'active' : ''}${dd.open ? ' open' : ''}`}
      >
        <a className='dropdown-toggle' onClick={dd.toggle} style={{ cursor: 'pointer' }}>
          <br />
          Vendor Payments
          <b className='caret' />
        </a>
        {dd.open && (
          <ul className='dropdown-menu' style={{ display: 'block' }}>
            {perms.prepaymentReader && (
              <li>
                <a style={{ borderBottom: '0px' }} href='#/VendorPayments/PrePaymentList'>
                  Prepayments
                </a>
              </li>
            )}
            {perms.vendorPenaltiesReader && (
              <li>
                <a style={{ borderBottom: '0px' }} href='#/VendorPayments/VendorPenaltyList'>
                  Vendor Penalties
                </a>
              </li>
            )}
          </ul>
        )}
      </li>
    ) : null;

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'property'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'property') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          {renderPropertyRecordDropdown(propertyRecordDD)}
          <li className={isActive('/Damages/history/') ? 'active' : ''}>
            <a href='#/Damages/history'>Damage History</a>
          </li>
          <li className={isActive('/Bids/history') ? 'active' : ''}>
            <a href='#/Bids/history'>Bid History</a>
          </li>
          {renderVendorPaymentsDropdown(vendorPaymentsDD)}
          {perms.propertyImageViewer && (
            <li>
              <a
                onClick={() =>
                  newWindow(`${imageViewerUrl}/?PropertyId=${property?.spiPropertyId ?? ''}`)
                }
                style={{ cursor: 'pointer' }}
              >
                Image Viewer
                <i className='ml-1 fa fa-external-link' />
              </a>
            </li>
          )}
          {perms.SafeSend && (
            <li>
              <a
                onClick={() =>
                  newWindow(
                    `${fileImporterURL}${
                      isOnOrderEntryPage()
                        ? ''
                        : `?mode=Property%20ID&id=${workOrder?.propertyLoanId ?? (workOrder as any)?.propertyId ?? ''}`
                    }`
                  )
                }
                style={{ cursor: 'pointer' }}
              >
                File Importer
                <i className='ml-1 fa fa-external-link' />
              </a>
            </li>
          )}
        </ul>
        {renderPropertyRightItems('View Texts')}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'workorder' || nav === 'audit' || nav === 'auditresults'
  // ══════════════════════════════════════════════════════════════════════════
  if (
    currentContext === 'workorder' ||
    currentContext === 'audit' ||
    currentContext === 'auditresults'
  ) {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          {/* Property Record dropdown — no Reconvey in workorder/audit/auditresults context (matches AngularJS) */}
          {renderPropertyRecordDropdown(propertyRecordDD, { includeReconvey: false })}

          {/* Overview dropdown */}
          <li
            ref={overviewDD.ref}
            onMouseEnter={overviewDD.onMouseEnter}
            onMouseLeave={overviewDD.onMouseLeave}
            className={`${
              isActive('/Audit/auditdashboard/overview') ||
              isActive('/Audit/auditdashboard/orderStatusTracker') ||
              isActive('/AuditRequest') ||
              isActive('/Audit/auditdashboard/results')
                ? 'active'
                : ''
            }${overviewDD.open ? ' open' : ''}`}
          >
            <a
              href='#/Audit/auditdashboard/overview'
              style={{ cursor: 'pointer' }}
              className={counts.questionFailureCount ? 'top-right-badge' : ''}
              data-badge={counts.questionFailureCount || undefined}
              onClick={overviewDD.toggle}
            >
              Overview
              <b className='caret' />
            </a>
            {overviewDD.open && (
              <ul className='dropdown-menu' style={{ display: 'block' }}>
                <li>
                  <a
                    style={{ borderBottom: '0px' }}
                    href='#/Audit/auditdashboard/orderStatusTracker?forceOrderStatusTracker=true'
                  >
                    Status Tracker
                  </a>
                </li>
                <li>
                  <a
                    style={{ borderBottom: '0px' }}
                    href='#/Audit/auditdashboard/workOrderWorkFlowHistory'
                  >
                    Work Order History
                  </a>
                </li>
                <li>
                  <a
                    style={{ borderBottom: '0px' }}
                    href='#/Audit/auditdashboard/workOrderFinancialWorkFlowHistory'
                  >
                    Work Order Financial History
                  </a>
                </li>
                {perms.viewWorkOrderDashboardTab && (
                  <li>
                    <a
                      style={{ borderBottom: '0px' }}
                      href='#/Audit/auditdashboard/workOrderDashboard'
                    >
                      Work Order Dashboard
                    </a>
                  </li>
                )}
              </ul>
            )}
          </li>

          {/* Damages dropdown */}
          <li
            ref={damagesDD.ref}
            onMouseEnter={damagesDD.onMouseEnter}
            onMouseLeave={damagesDD.onMouseLeave}
            className={`${
              isActive('/Damages/summary') ||
              isActive('/Damages/details/') ||
              isActive('/Damages/summary_inspection') ||
              isActive('/Damages/history/')
                ? 'active'
                : ''
            }${damagesDD.open ? ' open' : ''}`}
          >
            <a
              href='#/Damages/summary'
              className={counts.damageFailureCount ? 'top-right-badge' : ''}
              data-badge={counts.damageFailureCount || undefined}
              onClick={damagesDD.toggle}
            >
              Damages
              <b className='caret' />
            </a>
            {damagesDD.open && (
              <ul className='dropdown-menu' style={{ display: 'block' }}>
                <li>
                  <a style={{ borderBottom: '0px' }} href='#/Damages/history'>
                    Damage History
                  </a>
                </li>
              </ul>
            )}
          </li>

          {/* Bids dropdown */}
          <li
            ref={bidsDD.ref}
            onMouseEnter={bidsDD.onMouseEnter}
            onMouseLeave={bidsDD.onMouseLeave}
            className={`${
              isActive('/Bids/summary') ||
              isActive('/Bids/addbid') ||
              isActive('/Bids/detail') ||
              isActive('/Bids/approvaldetail') ||
              isActive('/Bids/approvaldisposition') ||
              isActive('/Bids/auditscrub') ||
              isActive('/Bids/biddetail') ||
              isActive('/Bids/biddisposition') ||
              isActive('/Bids/partials') ||
              isActive('/Bids/detail/') ||
              isActive('/Bids/history')
                ? 'active'
                : ''
            }${bidsDD.open ? ' open' : ''}`}
          >
            <a
              href='#/Bids/summary'
              className={counts.bidFailureCount ? 'top-right-badge' : ''}
              data-badge={counts.bidFailureCount || undefined}
              onClick={bidsDD.toggle}
            >
              Bids
              <b className='caret' />
            </a>
            {bidsDD.open && (
              <ul className='dropdown-menu' style={{ display: 'block' }}>
                <li>
                  <a style={{ borderBottom: '0px' }} href='#/Bids/history'>
                    Bid History
                  </a>
                </li>
              </ul>
            )}
          </li>

          {/* Tasks */}
          <li
            className={
              isActive('/Bids/tasksummary/') ||
              isActive('/Audit/addTask') ||
              isActive('/Bids/taskdetail/')
                ? 'active'
                : ''
            }
          >
            <a
              href='#/Bids/tasksummary'
              className={counts.taskFailureCount ? 'top-right-badge' : ''}
              data-badge={counts.taskFailureCount || undefined}
            >
              Tasks
            </a>
          </li>

          {/* Vendor Payments */}
          {renderVendorPaymentsDropdown(vendorPaymentsWoDD)}

          {/* Disputes */}
          {perms.disputeReader && (
            <li className={activeTab === 'Disputes' ? 'active' : ''}>
              <a
                href='#/Disputes/DisputeSummary'
                className={disputesCount ? 'top-right-badge' : ''}
                data-badge={disputesCount || undefined}
              >
                Disputes
              </a>
            </li>
          )}

          {/* Image Viewer (no permission check in workorder context) */}
          <li>
            <a
              onClick={() =>
                newWindow(`${imageViewerUrl}/?OrderId=${workOrder?.orderNumber ?? ''}`)
              }
              style={{ cursor: 'pointer' }}
            >
              Image Viewer
              <i className='ml-1 fa fa-external-link' />
            </a>
          </li>

          {/* File Importer */}
          {perms.SafeSend && (
            <li>
              <a
                onClick={() =>
                  newWindow(
                    `${fileImporterURL}?mode=Order%20Number&id=${workOrder?.orderNumber ?? ''}`
                  )
                }
                style={{ cursor: 'pointer' }}
              >
                File Importer
                <i className='ml-1 fa fa-external-link' />
              </a>
            </li>
          )}

          {/* Location */}
          <li className={isActive('/Location/geomap/') ? 'active' : ''}>
            <a href='#/Location/geomap'>Location</a>
          </li>

          {/* Back to Invoice */}
          {returnUrlInvoice && (
            <li>
              <a href={`#${returnUrlInvoice}`} style={{ cursor: 'pointer' }}>
                Back to Invoice
                <i className='fa fa-arrow-left' />
              </a>
            </li>
          )}
        </ul>
        {renderPropertyRightItems('View Text')}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'overallowables'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'overallowables') {
    return (
      <div className='subnav-container'>
        {perms.oaAdmin && (
          <ul className='nav sub-nav sub-nav-left'>
            <li className={isActive('/Overallowables/manage_client') ? 'active' : ''}>
              <a href='#/Overallowables/manage_client'>
                <br />
                Manage Clients
              </a>
            </li>
          </ul>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'vendorManagement' || 'companyMaintenance' || 'companyDetails' || 'serviceArea'
  // ══════════════════════════════════════════════════════════════════════════
  if (
    currentContext === 'vendorManagement' ||
    currentContext === 'companyMaintenance' ||
    currentContext === 'companyDetails' ||
    currentContext === 'serviceArea'
  ) {
    const vendorRightItems = (
      <ul className='nav sub-nav navbar-right sub-nav-right'>
        {(currentContext === 'companyMaintenance' || currentContext === 'serviceArea') &&
          perms.vendorTextReader && (
            <li className='property-texts-init'>
              <a onClick={toggleVendorTextsModal} style={{ cursor: 'pointer' }}>
                <i className='fa fa-comment-o' style={{ fontSize: '18px' }} /> View Texts
              </a>
            </li>
          )}
        {/* Legacy vendor "Add Text" (AddVendorText) removed per PR #204 — the
            Core Script variant below is now the sole path. */}
        {currentContext === 'serviceArea' && perms.vendorTextUpdaterCore && (
          <li className='property-texts-init'>
            <a
              href=''
              onClick={(e) => {
                e.preventDefault();
                navigateToAddVendorText(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <i className='fa fa-plus' style={{ fontSize: '18px' }} /> Add Text Core Script
            </a>
          </li>
        )}
        {currentContext === 'companyMaintenance' && perms.vendorCompanyNotes && (
          <li className='property-notes-init'>
            <a
              href=''
              onClick={(e) => {
                e.preventDefault();
                navigateToVendorNotes();
              }}
            >
              <i className='fa fa-pencil' /> Notes
            </a>
          </li>
        )}
      </ul>
    );

    return (
      <div className='subnav-container'>
        {/* Left items */}
        <ul className='nav sub-nav sub-nav-left'>
          <li className={activeTab === 'VendorMGMT' ? 'active' : ''}>
            <a href='#/VendorManagement/VendorManagement'>Vendor Management</a>
          </li>

          {/* Utilities dropdown */}
          <li
            ref={utilitiesDD.ref}
            onMouseEnter={utilitiesDD.onMouseEnter}
            onMouseLeave={utilitiesDD.onMouseLeave}
            className={`${activeTab === 'Utilities' ? 'active' : ''}${utilitiesDD.open ? ' open' : ''}`}
          >
            <a href='' className='dropdown-toggle' onClick={utilitiesDD.toggle}>
              <br />
              Utilities
              <b className='caret' />
            </a>
            {utilitiesDD.open && (
              <ul className='dropdown-menu verticle-menu' style={{ display: 'block' }}>
                {perms.communicationTool && (
                  <li>
                    <a
                      className='vmenu-inactive'
                      onClick={() => newWindow(communicationToolURL)}
                      style={{ cursor: 'pointer' }}
                    >
                      Communication Tool
                      <i className='fa fa-external-link' />
                    </a>
                  </li>
                )}
                <li>
                  <a
                    className='vmenu-inactive'
                    href='#/VendorManagement/UnlockMobileDevices'
                    onClick={clearQueryParams}
                  >
                    MOBILE
                  </a>
                </li>
                {/* Self Service Orders sub-menu */}
                <li
                  ref={selfServiceDD.ref}
                  onMouseEnter={selfServiceDD.onMouseEnter}
                  onMouseLeave={selfServiceDD.onMouseLeave}
                  className={`dropdown-submenu${selfServiceDD.open ? ' open' : ''}`}
                >
                  <a className='vmenu-inactive' href='' onClick={selfServiceDD.toggle}>
                    SELF SERVICE ORDERS
                  </a>
                  {selfServiceDD.open && (
                    <ul className='dropdown-menu' style={{ display: 'block' }}>
                      <li>
                        <a
                          className='vmenu-inactive'
                          href='#/VendorManagement/UploadIneligibleVendorList'
                          onClick={clearQueryParams}
                        >
                          Upload Vendors Ineligible to Participate File
                        </a>
                      </li>
                      <li>
                        <a
                          className='vmenu-inactive'
                          href='#/VendorManagement/SelfServiceOrders'
                          onClick={clearQueryParams}
                        >
                          Upload Self Service Orders
                        </a>
                      </li>
                      <li>
                        <a
                          className='vmenu-inactive'
                          href='#/VendorManagement/RemoveAllServiceOrders'
                          onClick={clearQueryParams}
                        >
                          Remove All Self-Service Orders
                        </a>
                      </li>
                    </ul>
                  )}
                </li>
                <li>
                  <a
                    className='vmenu-inactive'
                    href='#/VendorManagement/RemoveAllServiceOrders'
                    onClick={clearQueryParams}
                  >
                    Remove All Self-Service Orders
                  </a>
                </li>
                {/* Assignment sub-menu */}
                <li
                  ref={assignmentDD.ref}
                  onMouseEnter={assignmentDD.onMouseEnter}
                  onMouseLeave={assignmentDD.onMouseLeave}
                  className={`dropdown-submenu${assignmentDD.open ? ' open' : ''}`}
                >
                  <a className='vmenu-inactive' href='' onClick={assignmentDD.toggle}>
                    Assignment
                  </a>
                  {assignmentDD.open && (
                    <ul className='dropdown-menu' style={{ display: 'block' }}>
                      <li>
                        <a
                          className='vmenu-inactive'
                          href='#/VendorManagement/UploadRankingList'
                          onClick={clearQueryParams}
                        >
                          UPLOAD RANKING (ZIP) FILE
                        </a>
                      </li>
                      <li>
                        <a
                          className='vmenu-inactive'
                          href='#/VendorManagement/RankFileManagement'
                          onClick={clearQueryParams}
                        >
                          Rank Utility
                        </a>
                      </li>
                    </ul>
                  )}
                </li>
                <li>
                  <a
                    className='vmenu-inactive'
                    href='#/VendorManagement/VendorRegions'
                    onClick={clearQueryParams}
                  >
                    REGIONS
                  </a>
                </li>
                <li>
                  <a
                    className='vmenu-inactive'
                    href='#/VendorManagement/ZoneProfiles'
                    onClick={clearQueryParams}
                  >
                    Zone Profiles
                  </a>
                </li>
                <li>
                  <a
                    className='vmenu-inactive'
                    href='#/VendorManagement/VendorMemos/VendorMemosList'
                  >
                    Vendor Memos
                  </a>
                </li>
              </ul>
            )}
          </li>

          {/* Marketing dropdown */}
          <li
            ref={marketingDD.ref}
            onMouseEnter={marketingDD.onMouseEnter}
            onMouseLeave={marketingDD.onMouseLeave}
            className={`${activeTab === 'Marketing' ? 'active' : ''}${marketingDD.open ? ' open' : ''}`}
          >
            <a href='' className='dropdown-toggle' onClick={marketingDD.toggle}>
              <br />
              MARKETING
              <b className='caret' />
            </a>
            {marketingDD.open && (
              <ul className='dropdown-menu' style={{ display: 'block' }}>
                <li>
                  <a className='vmenu-inactive' href='#/VendorManagement/UploadOppMapsList'>
                    Upload Opportunity Map File
                  </a>
                </li>
                <li>
                  <a className='vmenu-inactive' href='#/VendorManagement/UploadZonesList'>
                    Upload Zone File
                  </a>
                </li>
              </ul>
            )}
          </li>

          {/* Admin dropdown (VM Admin) */}
          {perms.vmadmin && (
            <li
              ref={vmAdminDD.ref}
              onMouseEnter={vmAdminDD.onMouseEnter}
              onMouseLeave={vmAdminDD.onMouseLeave}
              className={`${activeTab === 'VMAdmin' ? 'active' : ''}${vmAdminDD.open ? ' open' : ''}`}
            >
              <a href='' className='dropdown-toggle' onClick={vmAdminDD.toggle}>
                <br />
                Admin
                <b className='caret' />
              </a>
              {vmAdminDD.open && (
                <ul className='dropdown-menu' style={{ display: 'block' }}>
                  {perms.unlockUser && (
                    <li>
                      <a className='vmenu-inactive' href='#/VendorManagement/UnlockUser'>
                        Unlock User
                      </a>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* File Importer (VM) */}
          {companyDetails?.company?.id && perms.SafeSend && perms.fileImportVendorCompany && (
            <li>
              <a
                onClick={() =>
                  newWindow(
                    `${fileImporterURL}?mode=Vendor%20Company&id=${companyDetails?.company?.id ?? ''}`
                  )
                }
                style={{ cursor: 'pointer' }}
              >
                File Importer
                <i className='fa fa-external-link' />
              </a>
            </li>
          )}
        </ul>
        {vendorRightItems}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'admin'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'admin') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          {!clientCodeDetail && (
            <li className={isActive('/Admin/Clients/ClientMatrix') ? 'active' : ''}>
              <a href='#/Admin/Clients/ClientMatrix'>
                <br />
                Client Matrix
              </a>
            </li>
          )}
          {clientCodeDetail && (
            <li className={isActive('/Admin/Clients/ClientMatrix') ? 'active' : ''}>
              <a href={`#/Admin/Clients/ClientMatrix?id=${clientCodeDetail}`}>
                <br />
                Client Matrix
              </a>
            </li>
          )}
          {clientCodeDetail && (
            <li
              className={
                isActive(`/Admin/Clients/clientCodeDetails/${clientCodeDetail}`) ? 'active' : ''
              }
            >
              <a href={`#/Admin/Clients/clientCodeDetails/${clientCodeDetail}`}>
                <br />
                Client Code Details
              </a>
            </li>
          )}
          {clientCode && (
            <li className={activeTab === 'ClientScheduleOptions' ? 'active' : ''}>
              <a
                href={`#/Admin/Clients/ClientScheduleOptions/ClientScheduleOptionsList/${clientCodeDetail}&${clientCode}`}
              >
                <br />
                Client Scheduling Options
              </a>
            </li>
          )}
          {clientCodeDetail && (
            <li className={activeTab === 'ClientAllowables' ? 'active' : ''}>
              <a href={`#/Admin/Clients/ClientAllowables?id=${clientCodeDetail}`}>
                <br />
                Client Allowables
              </a>
            </li>
          )}
          {perms.clientList && (
            <li className={isActive('/Admin/Clients/client') ? 'active' : ''}>
              <a href='#/Admin/Clients/client'>
                <br />
                Clients List
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'auditadmin'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'auditadmin') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          <li className={isActive('/Admin/Audit/Question/Questions') ? 'active' : ''}>
            <a href='#/Admin/Audit/Question/Questions' onClick={clearSearch}>
              <br />
              Questions
            </a>
          </li>
          <li className={isActive('/Admin/Audit/QuestionAttribute') ? 'active' : ''}>
            <a href='#/Admin/Audit/QuestionAttribute' onClick={clearSearch}>
              <br />
              Question Attributes
            </a>
          </li>
          <li className={isActive('/Admin/Audit/Answer/Answers') ? 'active' : ''}>
            <a href='#/Admin/Audit/Answer/Answers' onClick={clearSearch}>
              <br />
              Answers
            </a>
          </li>
          <li className={isActive('/Admin/Audit/Termset') ? 'active' : ''}>
            <a href='#/Admin/Audit/Termset' onClick={clearSearch}>
              <br />
              Termsets
            </a>
          </li>
          <li className={isActive('/Admin/Audit/Rule/Rules') ? 'active' : ''}>
            <a href='#/Admin/Audit/Rule/Rules' onClick={clearSearch}>
              <br />
              Audit Rules
            </a>
          </li>
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'categories'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'categories') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          <li className={isActive('/Admin/Categories/Categories') ? 'active' : ''}>
            <a href='#/Admin/Categories/Categories'>
              <br />
              Categories
            </a>
          </li>
          <li className={isActive('/Admin/Categories/BidTaskXRefList') ? 'active' : ''}>
            <a href='#/Admin/Categories/BidTaskXRefList'>
              <br />
              BidTaskXref List
            </a>
          </li>
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'components'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'components') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          {perms.componentsReader && (
            <li className={isActive('/Admin/Components/ChangeSource') ? 'active' : ''}>
              <a href='#/Admin/Components/ChangeSource'>
                <br />
                Change Source
              </a>
            </li>
          )}
          {perms.componentsReader && (
            <li className={isActive('/Admin/Components/ComponentSequence') ? 'active' : ''}>
              <a href='#/Admin/Components/ComponentSequence'>
                <br />
                Component Sequence
              </a>
            </li>
          )}
          {perms.componentsReader && (
            <li className={isActive('/Admin/Components/Components') ? 'active' : ''}>
              <a href='#/Admin/Components/Components'>
                <br />
                Components
              </a>
            </li>
          )}
          {perms.autoTextReader && (
            <li className={isActive('/Admin/Components/AutoTextMaintenance') ? 'active' : ''}>
              <a href='#/Admin/Components/AutoTextMaintenance'>
                <br />
                AutoText
              </a>
            </li>
          )}
          {perms.componentsReader && (
            <li className={isActive('/Admin/Components/ChangeKeyword') ? 'active' : ''}>
              <a href='#/Admin/Components/ChangeKeyword'>
                <br />
                Keyword Maintenance
              </a>
            </li>
          )}
          {perms.componentTest && (
            <li className={isActive('/Admin/Components/TestRendering') ? 'active' : ''}>
              <a href='#/Admin/Components/TestRendering'>
                <br />
                Test Rendering
              </a>
            </li>
          )}
          {perms.componentsReader && (
            <li className={isActive('/Admin/Components/OrdinanceText') ? 'active' : ''}>
              <a href='#/Admin/Components/OrdinanceText'>
                <br />
                Ordinance Text
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'catsubcat'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'catsubcat') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          <li className={isActive('/Invoice/CatSubCat/ChangeSource') ? 'active' : ''}>
            <a href='#/Invoice/CatSubCat/ChangeSource'>
              <br />
              Change Source
            </a>
          </li>
          <li className={isActive('/Invoice/CatSubCat/CategoryMapping') ? 'active' : ''}>
            <a href='#/Invoice/CatSubCat/CategoryMapping'>
              <br />
              Category Mapping
            </a>
          </li>
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'pricing'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'pricing') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          {perms.pricingadmincsreader && (
            <li className={activeTab === 'ChangeSource' ? 'active' : ''}>
              <a href='#/Admin/Pricing/ChangeSource'>
                <br />
                Change Source
              </a>
            </li>
          )}
          {perms.pricingadminclientreader && (
            <li className={activeTab === 'ClientRate' ? 'active' : ''}>
              <a href='#/Admin/Pricing/ClientPricing/ClientTaskRateLookup/ClientRateLegacySearch'>
                <br />
                Client Pricing
              </a>
            </li>
          )}
          {perms.PricingAdminTestRendering && (
            <li className={activeTab === 'TestRendering' ? 'active' : ''}>
              <a href='#/Admin/Pricing/TestRendering/TestRendering'>
                <br />
                Test Rendering
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'clientpricing'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'clientpricing') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          {perms.pricingAdminRateSetupReader && (
            <li
              ref={rateSetupDD.ref}
              onMouseEnter={rateSetupDD.onMouseEnter}
              onMouseLeave={rateSetupDD.onMouseLeave}
              className={`${activeTab === 'ClientRate' ? 'active' : ''}${rateSetupDD.open ? ' open' : ''}`}
            >
              <a
                href='#/Admin/Pricing/ClientPricing/ClientTaskRateLookup/ClientRateLegacySearch'
                className='dropdown-toggle'
                onClick={rateSetupDD.toggle}
              >
                <br />
                RATE SETUP
                <b className='caret' />
              </a>
              {rateSetupDD.open && (
                <ul
                  className='dropdown-menu'
                  style={{ display: 'block', padding: '0px', borderBottom: '0px' }}
                >
                  {perms.pricingAdminDefaultRateReader && (
                    <li>
                      <a
                        style={{ borderBottom: '0px' }}
                        href='#/Admin/Pricing/ClientPricing/DefaultRate/DefaultRate'
                      >
                        <br />
                        Default Rate
                      </a>
                    </li>
                  )}
                  {perms.pricingAdminTaskMapReader && (
                    <li>
                      <a
                        style={{ borderBottom: '0px' }}
                        href='#/Admin/Pricing/ClientPricing/ClientTaskmap/ClientTaskmapList'
                      >
                        <br />
                        Taskmap
                      </a>
                    </li>
                  )}
                  {perms.pricingAdminLoanTypeMapReader && (
                    <li>
                      <a
                        style={{ borderBottom: '0px' }}
                        href='#/Admin/Pricing/ClientPricing/ClientLoanTypemap/ClientLoanTypemapList'
                      >
                        <br />
                        LoanType Map
                      </a>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}
          {perms.pricingAdminRangeSetup && (
            <li
              className={isActive('/Admin/Pricing/ClientPricing/ClientRangeSetup') ? 'active' : ''}
            >
              <a href='#/Admin/Pricing/ClientPricing/ClientRangeSetup'>
                <br />
                RANGE SETUP
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'clientgrouping'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'clientgrouping') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          <li className={activeTab === 'StateGroup' ? 'active' : ''}>
            <a href='#/Admin/ClientGrouping/StateGroup/StateGroupList'>
              <br />
              State Group
            </a>
          </li>
          <li className={activeTab === 'CityGroup' ? 'active' : ''}>
            <a href='#/Admin/ClientGrouping/CityGroup/CityGroupList'>
              <br />
              City Group
            </a>
          </li>
          <li className={activeTab === 'CountyGroup' ? 'active' : ''}>
            <a href='#/Admin/ClientGrouping/CountyGroup/CountyGroupList'>
              <br />
              County Group
            </a>
          </li>
          <li className={activeTab === 'ZipCodeGroup' ? 'active' : ''}>
            <a href='#/Admin/ClientGrouping/ZipCodeGroup/ZipCodeGroupList'>
              <br />
              Zip Code Group
            </a>
          </li>
          <li className={activeTab === 'WorkCodeGroup' ? 'active' : ''}>
            <a href='#/Admin/ClientGrouping/WorkCodeGroup/WorkCodeGroupList'>
              <br />
              Work Code Group
            </a>
          </li>
          <li className={activeTab === 'AttributeGroup' ? 'active' : ''}>
            <a href='#/Admin/ClientGrouping/AttributeGrouping/AttributeGroupList'>
              <br />
              Attribute Group
            </a>
          </li>
          <li className={activeTab === 'PunchCodeGroup' ? 'active' : ''}>
            <a href='#/Admin/ClientGrouping/PunchCodeGroup/PunchCodeGroupList'>
              <br />
              PunchCode Group
            </a>
          </li>
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'scheduleoptions'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'scheduleoptions') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          <li className={activeTab === 'StandardSetup' ? 'active' : ''}>
            <a href='#/Admin/Scheduling/StandardSetup/StandardSetup'>
              <br />
              Standard Setup
            </a>
          </li>
        </ul>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // nav === 'allowable'
  // ══════════════════════════════════════════════════════════════════════════
  if (currentContext === 'allowable') {
    return (
      <div className='subnav-container'>
        <ul className='nav sub-nav sub-nav-left'>
          <li className={activeTab === 'AllowableGroup' ? 'active' : ''}>
            <a href='#/Admin/Allowables/AllowableGroup/AllowableGroupList'>
              <br />
              Allowable Group
            </a>
          </li>
          <li className={activeTab === 'AllowableCategories' ? 'active' : ''}>
            <a href='#/Admin/Allowables/AllowableCategories/AllowableCategoriesList'>
              <br />
              Allowable Categories
            </a>
          </li>
          <li className={activeTab === 'AllowableTaskCategories' ? 'active' : ''}>
            <a href='#/Admin/Allowables/AllowableTaskCategories/AllowableTaskCategoriesList'>
              <br />
              Allowable Task Categories
            </a>
          </li>
          <li className={activeTab === 'AllowableServices' ? 'active' : ''}>
            <a href='#/Admin/Allowables/AllowableServices/ServicesList'>
              <br />
              Services
            </a>
          </li>
          <li className={activeTab === 'Allowable' ? 'active' : ''}>
            <a href='#/Admin/Allowables/AllowableTasks/AllowableTaskList'>
              <br />
              Tasks
            </a>
          </li>
          {perms.allowablesUploader && (
            <li
              ref={allowableUploadsDD.ref}
              onMouseEnter={allowableUploadsDD.onMouseEnter}
              onMouseLeave={allowableUploadsDD.onMouseLeave}
              className={`${activeTab === 'AllowableUploads' ? 'active' : ''}${allowableUploadsDD.open ? ' open' : ''}`}
            >
              <a
                href='#'
                className='dropdown-toggle'
                onClick={(e) => {
                  e.preventDefault();
                  allowableUploadsDD.toggle();
                }}
              >
                <br />
                Uploads
                <b className='caret' />
              </a>
              {allowableUploadsDD.open && (
                <ul className='dropdown-menu' style={{ display: 'block' }}>
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableGroup/UploadAllowableGroupLimit'
                      >
                        Allowable Groups
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableGroup/UploadClientsLoanTypes'
                      >
                        Clients/Loan Types
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableServices/UploadAllowableServices'
                      >
                        Services
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableCategories/UploadAllowableCategories'
                      >
                        Allowable Categories
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableTaskCategories/UploadAllowableTaskCategories'
                      >
                        Allowable Task Categories
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableTasks/UploadAllowableTasks'
                      >
                        Tasks
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableTasks/UploadPunchCodes'
                      >
                        Tasks - Punch Codes
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableTasks/UploadTasktypes'
                      >
                        Tasks - Task Types
                      </a>
                    </li>
                  )}
                  {perms.allowablesUploader && (
                    <li>
                      <a
                        className='vmenu-inactive'
                        href='#/Admin/Allowables/AllowableTasks/UploadQuestiontypes'
                      >
                        Tasks - Question Types
                      </a>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}
        </ul>
      </div>
    );
  }

  // ── Fallback: unknown context — render empty spacer
  return (
    <div className='subnav-container'>
      <ul className='nav sub-nav navbar-right sub-nav-right'>
        <li>
          <a>&nbsp;</a>
        </li>
      </ul>
      <ul className='nav sub-nav sub-nav-left' />
    </div>
  );
}
